import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';
import 'swiper/css';

import './menu.js';
import './modal.js';
import './scripts.js';
import './canvas.js';
import './service-expertise.js';

gsap.registerPlugin(ScrollTrigger);

window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
window.Swiper = Swiper;

/**
 * Lignée de bâtisseurs timeline mobile/tablette
 *
 * La timeline devient un slider à 1024px et moins.
 */
document.addEventListener('DOMContentLoaded', () => {
	const timeline = document.querySelector('.js-lignee-timeline-swiper');

	if (!timeline || !window.Swiper) {
		return;
	}

	const mobileQuery = window.matchMedia('(max-width: 1024px)');
	let timelineSwiper = null;

	const initTimelineSwiper = () => {
		if (timelineSwiper || !mobileQuery.matches) {
			return;
		}

		timelineSwiper = new Swiper(timeline, {
			modules: [Autoplay],
			slidesPerView: 1,
			centeredSlides: true,
			spaceBetween: 40,
			speed: 520,
			initialSlide: 0,
			grabCursor: true,
			watchOverflow: true,
			autoplay: {
				delay: 3500,
				disableOnInteraction: false,
				pauseOnMouseEnter: true,
			},
		});
	};

	const destroyTimelineSwiper = () => {
		if (!timelineSwiper || mobileQuery.matches) {
			return;
		}

		timelineSwiper.destroy(true, true);
		timelineSwiper = null;
	};

	const handleTimelineMode = () => {
		if (mobileQuery.matches) {
			initTimelineSwiper();
			return;
		}

		destroyTimelineSwiper();
	};

	handleTimelineMode();

	if (typeof mobileQuery.addEventListener === 'function') {
		mobileQuery.addEventListener('change', handleTimelineMode);
	} else {
		mobileQuery.addListener(handleTimelineMode);
	}
});

/**
 * Études de cas : galerie "Mise de l'avant"
 */
document.addEventListener('DOMContentLoaded', () => {
	const sliders = document.querySelectorAll('.js-etude-de-cas-featured-slider');

	if (!sliders.length || !window.Swiper) {
		return;
	}

	sliders.forEach((slider) => {
		const gallery = slider.closest('.etude-de-cas-featured__gallery');
		const prevButton = gallery?.querySelector('[data-etude-featured-prev]');
		const nextButton = gallery?.querySelector('[data-etude-featured-next]');
		const count = gallery?.querySelector('[data-etude-featured-count]');

		const swiper = new Swiper(slider, {
			modules: [Navigation],
			slidesPerView: 1,
			spaceBetween: 32,
			speed: 520,
			watchOverflow: true,
			navigation: {
				prevEl: prevButton,
				nextEl: nextButton,
			},
		});

		if (!count) {
			return;
		}

		const updateCount = () => {
			count.textContent = `${swiper.realIndex + 1}/${swiper.slides.length}`;
		};

		updateCount();
		swiper.on('slideChange', updateCount);
	});
});

/**
 * Services catégorisés
 *
 * Gère les tabs de la section services sur la page d’accueil.
 * Au clic sur un service dans la navigation, le contenu correspondant
 * s’affiche dans le panneau de droite.
 */
document.addEventListener('DOMContentLoaded', () => {
	const sections = document.querySelectorAll('[data-services-categorises]');

	if (!sections.length) {
		return;
	}

	sections.forEach((section) => {
		const tabs = section.querySelectorAll('[data-service-tab]');
		const panels = section.querySelectorAll('[data-service-panel]');

		tabs.forEach((tab) => {
			tab.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();

				const scrollX = window.scrollX;
				const scrollY = window.scrollY;
				const targetId = tab.getAttribute('data-service-tab');

				if (!targetId) {
					return;
				}

				tabs.forEach((item) => {
					item.classList.remove('is-active');
					item.setAttribute('aria-selected', 'false');
				});

				panels.forEach((panel) => {
					panel.classList.remove('is-active');
					panel.classList.add('d-none');
					panel.hidden = true;
				});

				const targetPanel = section.querySelector(`#${CSS.escape(targetId)}`);

				tab.classList.add('is-active');
				tab.setAttribute('aria-selected', 'true');

				if (targetPanel) {
					targetPanel.classList.add('is-active');
					targetPanel.classList.remove('d-none');
					targetPanel.hidden = false;
					targetPanel.style.removeProperty('display');
				}

				requestAnimationFrame(() => {
					window.scrollTo(scrollX, scrollY);
				});
			});
		});
	});
});

/**
 * Services catégorisés mobile accordion
 *
 * Anime l'ouverture des <details> mobile.
 * Le <details> natif ouvre trop sec, donc on contrôle la hauteur en JS.
 * Quand un item ouvre, les autres se ferment automatiquement.
 */
document.addEventListener('DOMContentLoaded', () => {
	const mobileItems = document.querySelectorAll('.services-categorises__mobile-item');

	if (!mobileItems.length) {
		return;
	}

	const mobileQuery = window.matchMedia('(max-width: 997.98px)');

	mobileItems.forEach((item) => {
		const summary = item.querySelector('.services-categorises__mobile-tab');
		const panel = item.querySelector('.services-categorises__mobile-panel');

		if (!summary || !panel) {
			return;
		}

		panel.style.overflow = 'hidden';

		if (!item.open) {
			panel.style.height = '0px';
			panel.style.opacity = '0';
		} else {
			panel.style.height = 'auto';
			panel.style.opacity = '1';
		}

		summary.addEventListener('click', (event) => {
			if (!mobileQuery.matches) {
				return;
			}

			event.preventDefault();

			if (item.dataset.animating === 'true') {
				return;
			}

			item.dataset.animating = 'true';

			if (item.open) {
				const startHeight = panel.scrollHeight;

				panel.style.height = `${startHeight}px`;
				panel.style.opacity = '1';
				panel.getBoundingClientRect();

				panel.style.transition = 'height 420ms ease, opacity 280ms ease';
				panel.style.height = '0px';
				panel.style.opacity = '0';

				window.setTimeout(() => {
					item.open = false;
					panel.style.transition = '';
					panel.style.height = '0px';
					panel.style.opacity = '0';
					item.dataset.animating = 'false';
				}, 420);

				return;
			}

			mobileItems.forEach((otherItem) => {
				if (otherItem === item || !otherItem.open) {
					return;
				}

				const otherPanel = otherItem.querySelector('.services-categorises__mobile-panel');

				if (!otherPanel) {
					otherItem.open = false;
					return;
				}

				otherPanel.style.transition = '';
				otherPanel.style.height = `${otherPanel.scrollHeight}px`;
				otherPanel.style.opacity = '1';
				otherPanel.getBoundingClientRect();

				otherPanel.style.transition = 'height 420ms ease, opacity 280ms ease';
				otherPanel.style.height = '0px';
				otherPanel.style.opacity = '0';

				window.setTimeout(() => {
					otherItem.open = false;
					otherPanel.style.transition = '';
					otherPanel.style.height = '0px';
					otherPanel.style.opacity = '0';
					otherItem.dataset.animating = 'false';
				}, 420);
			});

			item.open = true;

			panel.style.height = '0px';
			panel.style.opacity = '0';
			panel.getBoundingClientRect();

			const endHeight = panel.scrollHeight;

			panel.style.transition = 'height 420ms ease, opacity 280ms ease';
			panel.style.height = `${endHeight}px`;
			panel.style.opacity = '1';

			window.setTimeout(() => {
				panel.style.transition = '';
				panel.style.height = 'auto';
				panel.style.opacity = '1';
				item.dataset.animating = 'false';
			}, 420);
		});
	});
});

/**
 * Réalisations tabs + modal image/vidéo
 *
 * Gère les onglets de la section réalisations.
 * Au clic sur une réalisation :
 * - ouvre la vidéo en modal si un lien vidéo existe
 * - sinon ouvre l’image en grand
 */
document.addEventListener('DOMContentLoaded', () => {
	const sections = document.querySelectorAll('[data-realisations-tabs]');

	if (!sections.length) {
		return;
	}

	sections.forEach((section) => {
		const tabs = section.querySelectorAll('[data-realisations-tab]');
		const panels = section.querySelectorAll('[data-realisations-panel]');

		tabs.forEach((tab) => {
			tab.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();

				const scrollX = window.scrollX;
				const scrollY = window.scrollY;
				const targetId = tab.getAttribute('data-realisations-tab');

				if (!targetId) {
					return;
				}

				tabs.forEach((item) => {
					item.classList.remove('is-active');
					item.setAttribute('aria-selected', 'false');
				});

				panels.forEach((panel) => {
					panel.classList.remove('is-active');
					panel.classList.add('d-none');
					panel.hidden = true;
					panel.style.setProperty('display', 'none', 'important');
				});

				const targetPanel = section.querySelector(`#${CSS.escape(targetId)}`);

				tab.classList.add('is-active');
				tab.setAttribute('aria-selected', 'true');

				if (targetPanel) {
					targetPanel.classList.add('is-active');
					targetPanel.classList.remove('d-none');
					targetPanel.hidden = false;
					targetPanel.style.removeProperty('display');
				}

				requestAnimationFrame(() => {
					window.scrollTo(scrollX, scrollY);
				});
			});
		});
	});

	const modalTriggers = document.querySelectorAll('[data-realisations-modal-src]');

	if (!modalTriggers.length) {
		return;
	}

	const modal = document.createElement('div');

	modal.className = 'realisations-modal';
	modal.hidden = true;

	modal.innerHTML = `
		<div class="realisations-modal__overlay" data-realisations-modal-close></div>
		<div class="realisations-modal__content" role="dialog" aria-modal="true">
			<button class="realisations-modal__close" type="button" data-realisations-modal-close aria-label="Fermer">
				×
			</button>
			<div class="realisations-modal__body"></div>
		</div>
	`;

	document.body.appendChild(modal);

	const modalBody = modal.querySelector('.realisations-modal__body');
	const closeButtons = modal.querySelectorAll('[data-realisations-modal-close]');

	const getEmbedUrl = (url) => {
		if (!url) {
			return '';
		}

		if (url.includes('player.vimeo.com/video/')) {
			return url;
		}

		if (url.includes('youtube.com/watch?v=')) {
			return url.replace('watch?v=', 'embed/');
		}

		if (url.includes('youtu.be/')) {
			const videoId = url.split('youtu.be/')[1].split('?')[0];

			return `https://www.youtube.com/embed/${videoId}`;
		}

		if (url.includes('vimeo.com/')) {
			const match = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);

			if (match && match[1]) {
				return `https://player.vimeo.com/video/${match[1]}?badge=0&autopause=0`;
			}
		}

		return url;
	};

	const openModal = (type, src, title = '') => {
		if (!src || !modalBody) {
			return;
		}

		if (type === 'video') {
			const embedUrl = getEmbedUrl(src);

			if (!embedUrl) {
				return;
			}

			modalBody.innerHTML = `
				<iframe
					src="${embedUrl}"
					title="${title || 'Vidéo'}"
					frameborder="0"
					allow="autoplay; fullscreen; picture-in-picture"
					allowfullscreen
				></iframe>
			`;
		} else {
			modalBody.innerHTML = `
				<img
					src="${src}"
					alt="${title || ''}"
				>
			`;
		}

		modal.hidden = false;
		document.body.classList.add('is-realisations-modal-open');
	};

	const closeModal = () => {
		modal.hidden = true;

		if (modalBody) {
			modalBody.innerHTML = '';
		}

		document.body.classList.remove('is-realisations-modal-open');
	};

	modalTriggers.forEach((trigger) => {
		trigger.addEventListener('click', (event) => {
			event.preventDefault();
			event.stopPropagation();

			const type = trigger.getAttribute('data-realisations-modal-type') || 'image';
			const src = trigger.getAttribute('data-realisations-modal-src');
			const title = trigger.getAttribute('data-realisations-modal-title') || '';

			openModal(type, src, title);
		});
	});

	closeButtons.forEach((button) => {
		button.addEventListener('click', closeModal);
	});

	document.addEventListener('keydown', (event) => {
		if (event.key === 'Escape' && !modal.hidden) {
			closeModal();
		}
	});
});

/**
 * Service proof tabs
 */
document.addEventListener('DOMContentLoaded', () => {
	const sections = document.querySelectorAll('[data-service-proof-tabs]');

	if (!sections.length) {
		return;
	}

	sections.forEach((section) => {
		const tabs = section.querySelectorAll('[data-service-proof-tab]');
		const panels = section.querySelectorAll('[data-service-proof-panel]');

		tabs.forEach((tab) => {
			tab.addEventListener('click', (event) => {
				event.preventDefault();

				const targetId = tab.getAttribute('data-service-proof-tab');
				const scrollY = window.scrollY;

				if (!targetId) {
					return;
				}

				tabs.forEach((item) => {
					item.classList.remove('is-active', 'text-primary');
					item.setAttribute('aria-selected', 'false');
				});

				panels.forEach((panel) => {
					panel.classList.remove('is-active');
					panel.classList.add('d-none');
					panel.hidden = true;
				});

				const targetPanel = section.querySelector(`#${CSS.escape(targetId)}`);

				tab.classList.add('is-active', 'text-primary');
				tab.setAttribute('aria-selected', 'true');

				if (targetPanel) {
					targetPanel.classList.add('is-active');
					targetPanel.classList.remove('d-none');
					targetPanel.hidden = false;
					targetPanel.style.removeProperty('display');
				}

				window.requestAnimationFrame(() => {
					window.scrollTo({
						top: scrollY,
						left: window.scrollX,
						behavior: 'auto',
					});
				});
			});
		});
	});
});

/**
 * Réalisations — vidéo Vimeo au hover desktop + clic mobile.
 */
document.addEventListener('DOMContentLoaded', () => {
	const cards = document.querySelectorAll('.realisations-card--featured.has-video-preview');

	if (!cards.length) {
		return;
	}

	const desktopQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

	const loadVimeoApi = () => {
		return new Promise((resolve) => {
			if (window.Vimeo && window.Vimeo.Player) {
				resolve();
				return;
			}

			const existingScript = document.querySelector(
				'script[src="https://player.vimeo.com/api/player.js"]'
			);

			if (existingScript) {
				existingScript.addEventListener('load', resolve, { once: true });
				return;
			}

			const script = document.createElement('script');

			script.src = 'https://player.vimeo.com/api/player.js';
			script.async = true;
			script.onload = resolve;

			document.body.appendChild(script);
		});
	};

	loadVimeoApi().then(() => {
		cards.forEach((card) => {
			const iframe = card.querySelector('.realisations-card__video iframe');

			if (!iframe || !window.Vimeo || !window.Vimeo.Player) {
				return;
			}

			const player = new window.Vimeo.Player(iframe);

			player.ready().then(() => {
				player.setMuted(true).catch(() => {});
				player.pause().catch(() => {});
				player.setCurrentTime(0).catch(() => {});
				card.classList.add('is-video-ready');
			});

			const playVideo = (force = false) => {
				if (!force && !desktopQuery.matches) {
					return;
				}

				card.classList.add('is-video-playing');

				player.ready().then(() => {
					player.setMuted(true).catch(() => {});
					player.play().catch((error) => {
						console.warn('Vimeo play failed:', error);
					});
				});
			};

			const pauseVideo = () => {
				card.classList.remove('is-video-playing');

				player.pause().catch(() => {});
				player.setCurrentTime(0).catch(() => {});
			};

			card.addEventListener('mouseenter', () => playVideo(false));
			card.addEventListener('mouseleave', pauseVideo);
			card.addEventListener('focusin', () => playVideo(false));
			card.addEventListener('focusout', pauseVideo);

			card.addEventListener('click', (event) => {
				if (desktopQuery.matches) {
					return;
				}

				event.preventDefault();

				if (card.classList.contains('is-video-playing')) {
					pauseVideo();
					return;
				}

				playVideo(true);
			});
		});
	});
});

/**
 * Counters
 *
 * Anime les chiffres quand la section de statistiques entre dans la vue.
 * Le compteur se réinitialise quand on quitte la section, donc il repart
 * autant en descendant qu’en remontant.
 */
document.addEventListener('DOMContentLoaded', () => {
	const counterSections = document.querySelectorAll('[data-counter-section]');

	if (!counterSections.length) {
		return;
	}

	const formatNumber = (number) => {
		return new Intl.NumberFormat('fr-CA').format(number);
	};

	const resetCounter = (counter) => {
		if (counter.animationFrame) {
			cancelAnimationFrame(counter.animationFrame);
		}

		counter.textContent = '0';
		counter.dataset.counterAnimated = 'false';
	};

	const animateCounter = (counter) => {
		const target = parseInt(counter.getAttribute('data-count'), 10);

		if (Number.isNaN(target)) {
			return;
		}

		if (counter.animationFrame) {
			cancelAnimationFrame(counter.animationFrame);
		}

		counter.dataset.counterAnimated = 'true';

		const duration = 1400;
		const startTime = performance.now();

		const update = (currentTime) => {
			const elapsed = currentTime - startTime;
			const progress = Math.min(elapsed / duration, 1);
			const easedProgress = 1 - Math.pow(1 - progress, 3);
			const currentValue = Math.floor(easedProgress * target);

			counter.textContent = formatNumber(currentValue);

			if (progress < 1) {
				counter.animationFrame = requestAnimationFrame(update);
			} else {
				counter.textContent = formatNumber(target);
			}
		};

		counter.animationFrame = requestAnimationFrame(update);
	};

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				const counterSection =
					entry.target.closest('[data-counter-section]') || entry.target;

				const counters = counterSection.querySelectorAll('.js-counter');

				if (entry.isIntersecting) {
					counters.forEach((counter) => {
						animateCounter(counter);
					});
				} else {
					counters.forEach((counter) => {
						resetCounter(counter);
					});
				}
			});
		},
		{
			threshold: 0.35,
		}
	);

	counterSections.forEach((section) => {
		const trigger =
			section.querySelector(
				'.careers-lignee__retention-number, .lignee-batisseurs__retention-number'
			) || section;

		const counters = section.querySelectorAll('.js-counter');

		counters.forEach((counter) => {
			resetCounter(counter);
		});

		observer.observe(trigger);
	});
});

// Effet de profondeur plus marqué sur le titre ADN du hero
// Les deux ADN bougent : le tracé outline + le texte principal.
// On garde le translateY(-12%) du outline pour conserver sa position de base.
const depthTitle = document.querySelector('[data-depth-title]');
const homeHeader = document.querySelector('.home-header__inner');

if (depthTitle && homeHeader) {
	const titleStroke = depthTitle.querySelector('.home-header__title-stroke');
	const titleMain = depthTitle.querySelector('.home-header__title');

	let animationFrame = null;

	const moveTitle = (event) => {
		const rect = homeHeader.getBoundingClientRect();

		const x = (event.clientX - rect.left) / rect.width - 0.5;
		const y = (event.clientY - rect.top) / rect.height - 0.5;

		const absX = Math.abs(x);
		const absY = Math.abs(y);

		if (animationFrame) {
			cancelAnimationFrame(animationFrame);
		}

		animationFrame = requestAnimationFrame(() => {
			if (titleStroke) {
				titleStroke.style.transform = `translate(${x * -95}px, calc(-12% + ${y * -60}px)) scaleX(${1.14 + absX * 0.16}) scaleY(${1 + absY * 0.08}) rotate(${x * -2.5}deg)`;
			}

			if (titleMain) {
				titleMain.style.transform = `translate(${x * 42}px, ${y * 28}px) scale(${1 + absX * 0.035}) rotate(${x * 0.8}deg)`;
			}
		});
	};

	const resetTitle = () => {
		if (animationFrame) {
			cancelAnimationFrame(animationFrame);
		}

		animationFrame = requestAnimationFrame(() => {
			if (titleStroke) {
				titleStroke.style.transform = 'translateY(-12%) scaleX(1.14)';
			}

			if (titleMain) {
				titleMain.style.transform = 'translate(0, 0) scale(1) rotate(0deg)';
			}
		});
	};

	homeHeader.addEventListener('mousemove', moveTitle);
	homeHeader.addEventListener('mouseleave', resetTitle);
}

// Animation d'entrée du loup et de l'ouvrier dans le hero
// On anime les images seulement, pas les wrappers,
// pour ne pas écraser le transform SCSS qui place les images au bon endroit.
const heroWolf = document.querySelector('.home-header__visual--left img');
const heroWorker = document.querySelector('.home-header__visual--right img');

if (window.gsap && heroWolf && heroWorker) {
	gsap.from(heroWolf, {
		x: -180,
		opacity: 0,
		duration: 1.25,
		delay: 0.25,
		ease: 'power3.out',
		clearProps: 'transform',
	});

	gsap.from(heroWorker, {
		x: 180,
		opacity: 0,
		duration: 1.25,
		delay: 0.65,
		ease: 'power3.out',
		clearProps: 'transform',
	});
}

// Animation douce du contenu central du hero
const heroContent = document.querySelector('.home-header__content');

if (window.gsap && heroContent) {
	const heroSubtitle = heroContent.querySelector('.home-header__subtitle');
	const heroTextLeft = heroContent.querySelector('.home-header__text-column--left');
	const heroTextRight = heroContent.querySelector('.home-header__text-column--right');

	const heroTimeline = gsap.timeline({
		delay: 0.45,
		defaults: {
			duration: 0.8,
			ease: 'power3.out',
		},
	});

	if (heroSubtitle) {
		heroTimeline.from(heroSubtitle, {
			y: 24,
			opacity: 0,
		});
	}

	if (heroTextLeft) {
		heroTimeline.from(
			heroTextLeft,
			{
				x: -32,
				opacity: 0,
			},
			'-=0.45'
		);
	}

	if (heroTextRight) {
		heroTimeline.from(
			heroTextRight,
			{
				x: 32,
				opacity: 0,
			},
			'-=0.65'
		);
	}
}

// Animation organique des ronds de lumière
document.addEventListener('DOMContentLoaded', () => {
	const lights = document.querySelectorAll('.js-animated-light');

	if (!window.gsap || !lights.length) {
		return;
	}

	lights.forEach((light, index) => {
		const isLeft = light.classList.contains('js-animated-light--left');
		const direction = isLeft ? -1 : 1;
		const distance = isLeft ? 1.8 : 1;

		gsap.set(light, {
			x: 0,
			y: 0,
			scale: 1,
			rotate: 0,
			opacity: 1,
			transformOrigin: '50% 50%',
			willChange: 'transform',
		});

		const lightTimeline = gsap.timeline({
			repeat: -1,
			defaults: {
				ease: 'sine.inOut',
			},
		});

		lightTimeline
			.to(light, {
				x: 120 * direction * distance,
				y: -135,
				scale: 1.18,
				rotate: 4 * direction,
				duration: 7.5 + index,
			})
			.to(light, {
				x: 60 * direction * distance,
				y: 210,
				scale: 0.92,
				rotate: -3 * direction,
				duration: 7.2 + index,
			})
			.to(light, {
				x: -200 * direction * distance,
				y: 95,
				scale: 1.14,
				rotate: 3 * direction,
				duration: 7.8 + index,
			})
			.to(light, {
				x: -160 * direction * distance,
				y: -130,
				scale: 0.96,
				rotate: -2 * direction,
				duration: 7.1 + index,
			})
			.to(light, {
				x: 0,
				y: 0,
				scale: 1,
				rotate: 0,
				duration: 7.4 + index,
			});
	});
});

/**
 * Animation de tracé des lignes topo SVG.
 */
document.addEventListener('DOMContentLoaded', () => {
	const topoWrappers = document.querySelectorAll('.js-topo-svg');

	if (!topoWrappers.length) {
		console.warn('Aucun wrapper topo SVG trouvé');
		return;
	}

	topoWrappers.forEach((wrapper) => {
		const topoLines = wrapper.querySelectorAll(
			'svg path, svg line, svg polyline, svg circle, svg ellipse'
		);

		if (!topoLines.length) {
			console.warn('Aucune ligne SVG trouvée dans', wrapper);
			return;
		}

		topoLines.forEach((line, index) => {
			if (typeof line.getTotalLength !== 'function') {
				return;
			}

			const length = line.getTotalLength();

			line.style.strokeDasharray = length;
			line.style.strokeDashoffset = length;
			line.style.animation = 'none';

			line.getBoundingClientRect();

			line.style.animation = `topoSvgLineDraw 5.5s ease-out ${0.45 + index * 0.035}s forwards`;
		});
	});
});

/**
 * Reveal sections on scroll
 */
document.addEventListener('DOMContentLoaded', () => {
	const revealSections = document.querySelectorAll('.js-reveal-section');

	if (!revealSections.length) {
		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					entry.target.classList.add('is-in-view');
					observer.unobserve(entry.target);
				}
			});
		},
		{
			threshold: 0.04,
			rootMargin: '0px 0px -18% 0px',
		}
	);

	revealSections.forEach((section) => {
		observer.observe(section);
	});
});

/**
 * Animation lettre par lettre des titres outline.
 * Poste détail + Carrières.
 *
 * Les spans sont créés au chargement,
 * mais l'animation démarre seulement quand le bon trigger entre dans le viewport.
 *
 * Pour careers-postes, on déclenche sur le kicker visible plutôt que sur
 * le gros titre outline positionné en absolute, afin d'éviter que
 * l'animation parte trop tôt.
 */
document.addEventListener('DOMContentLoaded', () => {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !window.ScrollTrigger || reduceMotion) {
		return;
	}

	const outlines = document.querySelectorAll(
		'.single-poste__outline, .careers-team__outline, .careers-engagement__outline, .careers-postes__outline, .careers-cta__outline-title'
	);

	outlines.forEach((outline) => {
		const outlineText = outline.textContent.trim();

		if (!outlineText) {
			return;
		}

		const letters = [];
		const words = outlineText.split(/\s+/);

		outline.setAttribute('aria-label', outlineText);
		outline.removeAttribute('aria-hidden');
		outline.textContent = '';

		words.forEach((word, wordIndex) => {
			const wordSpan = document.createElement('span');

			wordSpan.setAttribute('aria-hidden', 'true');
			wordSpan.style.display = 'inline-block';
			wordSpan.style.whiteSpace = 'nowrap';

			Array.from(word).forEach((letter) => {
				const span = document.createElement('span');

				span.textContent = letter;
				span.style.display = 'inline-block';

				wordSpan.appendChild(span);
				letters.push(span);
			});

			outline.appendChild(wordSpan);

			if (wordIndex < words.length - 1) {
				outline.appendChild(document.createTextNode(' '));
			}
		});

		let trigger = outline;

		if (outline.classList.contains('careers-postes__outline')) {
			trigger =
				outline
					.closest('.careers-postes__header')
					?.querySelector('.careers-postes__kicker') || outline;
		}

		gsap.from(letters, {
			x: -28,
			y: 18,
			opacity: 0,
			scale: 0.92,
			duration: 0.7,
			ease: 'power3.out',
			stagger: 0.065,
			scrollTrigger: {
				trigger,
				start: 'top 75%',
				once: true,
			},
		});
	});
});

/**
 * Careers team cards parallax.
 */
document.addEventListener('DOMContentLoaded', () => {
	const careersTeam = document.querySelector('.careers-team');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !window.ScrollTrigger || !careersTeam || reduceMotion) {
		return;
	}

	const cards = careersTeam.querySelectorAll('.careers-team__card');

	cards.forEach((card, index) => {
		const directions = [-1, 1, 0.7, -0.7];
		const direction = directions[index % directions.length];

		gsap.fromTo(
			card,
			{
				y: direction * 70,
			},
			{
				y: direction * -70,
				ease: 'none',
				scrollTrigger: {
					trigger: careersTeam,
					start: 'top bottom',
					end: 'bottom top',
					scrub: 1.2,
				},
			}
		);
	});
});

/**
 * Header ellipse mouse parallax.
 * Utilisé sur la page Poste détail et la page Carrières.
 * Aucun fade / aucune animation d'entrée.
 */
document.addEventListener('DOMContentLoaded', () => {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || reduceMotion) {
		return;
	}

	const ellipseParallaxItems = [
		{
			ellipse: document.querySelector('.single-poste__header-ellipse'),
			container: document.querySelector('.single-poste__header'),
		},
		{
			ellipse: document.querySelector('.careers-team__ellipse'),
			container: document.querySelector('.careers-team'),
		},
	];

	ellipseParallaxItems.forEach(({ ellipse, container }) => {
		if (!ellipse || !container) {
			return;
		}

		gsap.set(ellipse, {
			opacity: 1,
			x: 0,
			y: 0,
		});

		container.addEventListener('mousemove', (event) => {
			const bounds = container.getBoundingClientRect();
			const progressX = ((event.clientX - bounds.left) / bounds.width) - 0.5;
			const progressY = ((event.clientY - bounds.top) / bounds.height) - 0.5;

			gsap.to(ellipse, {
				x: progressX * 300,
				y: progressY * 180,
				opacity: 1,
				duration: 0.8,
				ease: 'power2.out',
				overwrite: true,
			});
		});

		container.addEventListener('mouseleave', () => {
			gsap.to(ellipse, {
				x: 0,
				y: 0,
				opacity: 1,
				duration: 1,
				ease: 'power2.out',
				overwrite: true,
			});
		});
	});
});

/**
 * Formidable custom error message.
 */
function translateFormidableErrors() {
	const errorMessages = document.querySelectorAll('.frm_error_style');

	if (!errorMessages.length) {
		return;
	}

	errorMessages.forEach((message) => {
		const text = message.textContent.trim();

		if (text === 'There was a problem with your submission. Errors are marked below.') {
			message.textContent =
				'Il y a un problème avec votre soumission. Veuillez corriger les champs indiqués ci-dessous.';
		}
	});
}

document.addEventListener('DOMContentLoaded', () => {
	translateFormidableErrors();

	const observer = new MutationObserver(() => {
		translateFormidableErrors();
	});

	observer.observe(document.body, {
		childList: true,
		subtree: true,
	});
});

document.addEventListener('DOMContentLoaded', () => {
	const select = document.querySelector('#field_o4dq72');

	if (!select) {
		return;
	}

	const placeholder = select.querySelector('.frm-select-placeholder');

	if (placeholder) {
		placeholder.disabled = true;
	}
});

document.addEventListener('DOMContentLoaded', () => {
	const selects = document.querySelectorAll('.single-poste__form select');

	selects.forEach((select) => {
		if (select.dataset.choicesInitialized) {
			return;
		}

		new Choices(select, {
			searchEnabled: false,
			shouldSort: false,
			itemSelectText: '',
			allowHTML: false,
		});

		select.dataset.choicesInitialized = 'true';
	});
});

/**
 * Generic reveal animations.
 *
 * Reusable classes:
 * - .js-anim-up
 * - .js-anim-down
 * - .js-anim-left
 * - .js-anim-right
 *
 * Optional:
 * - data-anim-delay="0.12"
 * - data-anim-duration="0.86"
 */
document.addEventListener('DOMContentLoaded', () => {
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !window.ScrollTrigger || reduceMotion) {
		return;
	}

	const animations = {
		'js-anim-up': { y: 24 },
		'js-anim-down': { y: -28 },
		'js-anim-left': { x: -30 },
		'js-anim-right': { x: 30 },
	};

	Object.entries(animations).forEach(([className, movement]) => {
		document.querySelectorAll(`.${className}`).forEach((element) => {
			gsap.from(element, {
				...movement,
				opacity: 0,
				duration: parseFloat(element.dataset.animDuration || '0.72'),
				delay: parseFloat(element.dataset.animDelay || '0'),
				ease: 'power3.out',
				scrollTrigger: {
					trigger: element,
					start: 'top 86%',
					once: true,
				},
			});
		});
	});
});

/**
 * Contact info ellipse mouse parallax.
 */
document.addEventListener('DOMContentLoaded', () => {
	const contactInfoEllipse = document.querySelector('.contact-info__ellipse');
	const contactInfo = document.querySelector('.contact-info__inner');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !contactInfoEllipse || !contactInfo || reduceMotion) {
		return;
	}

	contactInfo.addEventListener('mousemove', (event) => {
		const bounds = contactInfo.getBoundingClientRect();

		const progressX =
			((event.clientX - bounds.left) / bounds.width) - 0.5;

		const progressY =
			((event.clientY - bounds.top) / bounds.height) - 0.5;

		gsap.to(contactInfoEllipse, {
			x: progressX * 64,
			y: progressY * 40,
			duration: 1.15,
			ease: 'sine.out',
			overwrite: true,
		});
	});

	contactInfo.addEventListener('mouseleave', () => {
		gsap.to(contactInfoEllipse, {
			x: 0,
			y: 0,
			duration: 1.4,
			ease: 'sine.inOut',
			overwrite: true,
		});
	});
});


document.addEventListener('DOMContentLoaded', () => {
	const titles = document.querySelectorAll('.js-title-gradient-reveal');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !titles.length || reduceMotion) {
		return;
	}

	titles.forEach((title) => {
		const timeline = gsap.timeline({
			delay: 0.2,
		});

		timeline.to(title, {
			backgroundSize: '100% 100%',
			duration: 1.65,
			ease: 'power3.inOut',
		});

		timeline.to(
			title,
			{
				WebkitTextStrokeColor: 'rgba(91, 186, 234, 0)',
				duration: 0.95,
				ease: 'power2.out',
			},
			0.75
		);
	});
});

document.addEventListener('DOMContentLoaded', () => {
	const hero = document.querySelector('.careers-hero');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !window.ScrollTrigger || !hero || reduceMotion) {
		return;
	}

	gsap.fromTo(
		hero,
		{
			backgroundPosition: '50% 50%',
		},
		{
			backgroundPosition: '50% 58%',
			ease: 'none',
			scrollTrigger: {
				trigger: hero,
				start: 'top top',
				end: 'bottom top',
				scrub: 1.4,
			},
		}
	);
});

document.addEventListener('DOMContentLoaded', () => {
	const careersEngagementImage = document.querySelector('.careers-engagement__image');
	const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	if (!window.gsap || !window.ScrollTrigger || !careersEngagementImage || reduceMotion) {
		return;
	}

	gsap.from(careersEngagementImage, {
		clipPath: 'inset(0 0 0 100%)',
		duration: 1.1,
		ease: 'power3.out',
		scrollTrigger: {
			trigger: careersEngagementImage,
			start: 'top 85%',
			once: true,
		},
	});
});


//// A propos
const deploiementSlider = document.querySelector('.a-propos-deploiement__images');

if (deploiementSlider) {
	let swiper = null;

	const initDeploiementSlider = () => {
		if (window.innerWidth <= 767 && !swiper) {
			swiper = new Swiper(deploiementSlider, {
				slidesPerView: 1.15,
				spaceBetween: 16,
			});
		} else if (window.innerWidth > 767 && swiper) {
			swiper.destroy(true, true);
			swiper = null;
		}
	};

	initDeploiementSlider();
	window.addEventListener('resize', initDeploiementSlider);
}
